//
// Orlistat 120mg (Xenical®) PGD — AHT weight management
// Source: AHT_Orlistat_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis — from source document:
//   Up to 84 capsules per consultation (one month at 120mg TDS).
//   Further supply requires a monitoring review.
//   Stopping rule: discontinue if <5% weight loss after 12 weeks.

import type { PGD } from "../../types";

export const pgdAhtWtOrlistat: PGD = {
  id: "pgd-aht-wt-orlistat",
  reference: "AHT-WT-ORL",
  version: "1.0",
  drugRef: "orlistat",
  indication: "Weight management — orlistat 120mg (Xenical®) oral hard capsules",
  validFrom: "",         // TODO: insert valid from date when signed
  expiryDate: "",        // TODO: insert expiry date when signed
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 74 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
    notes: "No dose adjustment required for age, gender, or ethnicity. Minimal systemic absorption — no adjustment for renal/hepatic impairment (use with caution; limited data in severe impairment).",
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "remote_permitted",
  },

  duration: {
    days: 28,
    comment: "Up to 84 capsules per consultation (120mg three times daily = 84 capsules/28 days). Further supply requires monitoring review.",
  },

  doseBands: [
    {
      id: "orl-standard",
      resolveBy: "single",
      dose: 120,
      frequency: "Three times daily with meals",
      comment: "One 120mg capsule immediately before, during, or up to one hour after each main meal containing fat. Omit dose if meal is missed or contains no fat. Doses above 120mg TDS have not been shown to provide additional benefit.",
      formulations: [
        {
          formulationId: "orlistat-120mg-capsule",
          instruction: "Take one 120mg capsule immediately before, during, or within 1 hour of each main meal containing fat. Omit the dose if a meal is missed or contains no fat.",
          quantity: { count: 84, label: "84 × orlistat 120mg capsules (28-day supply)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 21, max: 84 },
        },
      ],
    },
  ],

  preSupplyChecks: [
    // EXCLUSION CRITERIA — Source: Step 2 of AHT Orlistat pathway v1.0
    {
      id: "ex-bmi-below-28",
      question: "Is the patient's BMI below 28 kg/m²?",
      hint: "Orlistat requires BMI ≥28 kg/m².",
      autoAnswerFrom: "bmiBelowThreshold28",
    },
    {
      id: "ex-bmi-28-30-no-comorbidity",
      question: "Is the patient's BMI 28–30 kg/m² without a weight-related comorbidity?",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity28",
    },
    {
      id: "ex-age-under-18",
      question: "Is the patient under 18 years of age?",
      autoAnswerFrom: "age_under_18",
    },
    {
      id: "ex-hypersensitivity-orlistat",
      question: "Does the patient have known hypersensitivity to orlistat or any excipient of Xenical®?",
    },
    {
      id: "ex-chronic-malabsorption",
      question: "Does the patient have chronic malabsorption syndrome?",
    },
    {
      id: "ex-cholestasis",
      question: "Does the patient have cholestasis?",
      autoAnswerFrom: "q16Cholestasis",
    },
    {
      id: "ex-severe-hepatic-liver-disease",
      question: "Does the patient have severe hepatic impairment or active liver disease?",
    },
    {
      id: "ex-severe-renal-oxalate",
      question: "Does the patient have severe or end-stage renal disease or a history of oxalate nephropathy?",
      autoAnswerFrom: "q7SevereRenal",
    },
    {
      id: "ex-ciclosporin",
      question: "Is the patient currently taking ciclosporin?",
      hint: "Co-administration not recommended.",
      autoAnswerFrom: "q17Ciclosporin",
    },
    {
      id: "ex-oral-anticoagulants-no-inr",
      question: "Is the patient currently taking oral anticoagulants (e.g. warfarin) without capacity for INR monitoring?",
    },
    {
      id: "ex-antiepileptics-no-monitoring",
      question: "Is the patient currently taking antiepileptic drugs without appropriate monitoring in place?",
    },
    {
      id: "ex-antiretrovirals-no-oversight",
      question: "Is the patient currently taking antiretroviral medicines for HIV without specialist oversight?",
    },
    {
      id: "ex-acarbose",
      question: "Is the patient currently taking acarbose?",
      hint: "Co-administration not recommended (no formal pharmacokinetic study).",
    },
    {
      id: "ex-concurrent-weight-loss-agents",
      question: "Is the patient currently using other weight loss agents?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-endocrine-obesity",
      question: "Is the patient's obesity secondary to an endocrine condition under specialist management?",
      autoAnswerFrom: "sharedEndocrineObesity",
    },
    {
      id: "ex-virtual-consultation",
      question: "Is this a virtual or online consultation (for initial supply)?",
      hint: "Initial supply must be face-to-face at a registered GPhC/CQC premises.",
      autoAnswerFrom: "face_to_face_confirmed",
    },
  ],

  interactions: [
    {
      id: "int-ciclosporin",
      interactionName: "Ciclosporin",
      question: "Is the patient taking ciclosporin?",
      hint: "Reduced ciclosporin plasma levels — co-administration not recommended; if unavoidable, monitor frequently.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: true,
      managementPlanRequired: false,
    },
    {
      id: "int-warfarin-anticoagulants",
      interactionName: "Warfarin or oral anticoagulants",
      question: "Is the patient taking warfarin or another oral anticoagulant?",
      hint: "Orlistat may increase anticoagulant effect — monitor INR.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-levothyroxine",
      interactionName: "Levothyroxine",
      question: "Is the patient taking levothyroxine?",
      hint: "Reduced absorption — separate administration times; monitor thyroid function.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-antiepileptics",
      interactionName: "Antiepileptic drugs",
      question: "Is the patient taking antiepileptic drugs (valproate, lamotrigine)?",
      hint: "Reduced absorption — monitor seizure frequency and severity.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-antiretrovirals",
      interactionName: "Antiretrovirals for HIV",
      question: "Is the patient taking antiretroviral medicines for HIV?",
      hint: "Reduced absorption — advise HIV specialist oversight.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-amiodarone",
      interactionName: "Amiodarone",
      question: "Is the patient taking amiodarone?",
      hint: "Modest reduction in plasma amiodarone levels — reinforce clinical and ECG monitoring.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-oral-contraceptives",
      interactionName: "Oral contraceptives",
      question: "Is the patient taking oral contraceptives?",
      hint: "No direct pharmacokinetic interaction; indirect reduction possible with severe diarrhoea — advise additional contraception if severe diarrhoea occurs.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-antidepressants-antipsychotics",
      interactionName: "Antidepressants, antipsychotics, or benzodiazepines",
      question: "Is the patient taking antidepressants, antipsychotics (including lithium), or benzodiazepines?",
      hint: "Case reports of reduced efficacy — initiate with caution.",
      affectedDrugs: ["orlistat"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
  ],

  cautions: [
    {
      id: "caut-high-fat-diet",
      text: "High-fat diet substantially increases GI side effects — reinforce dietary guidance before supply",
      adviceText: "Limit fat intake to approximately 30% of total calories (~67g fat/day on 2,000 kcal diet). Distribute evenly across three main meals.",
    },
    {
      id: "caut-ckd-oxalate",
      text: "CKD or volume depletion: oxalate nephropathy risk — advise adequate hydration; monitor renal function",
    },
    {
      id: "caut-levothyroxine",
      text: "Levothyroxine: reduced absorption — separate administration times; monitor thyroid function",
    },
    {
      id: "caut-t2dm-antidiabetics",
      text: "T2DM on antidiabetics: weight loss may improve glycaemia — dose reduction of antidiabetic may be required; advise GP review",
    },
    {
      id: "caut-amiodarone",
      text: "Amiodarone: modest reduction in plasma levels — reinforce ECG and clinical monitoring",
    },
    {
      id: "caut-antidepressants-antipsychotics",
      text: "Antidepressants, antipsychotics (including lithium), benzodiazepines: case reports of reduced efficacy — initiate with caution",
    },
    {
      id: "caut-fat-soluble-vitamins",
      text: "Fat-soluble vitamins (A, D, E, K): absorption reduced — advise multivitamin at bedtime (≥2 hours after orlistat)",
    },
    {
      id: "caut-rectal-bleeding",
      text: "Rectal bleeding: investigate and refer to GP",
    },
    {
      id: "caut-severe-diarrhoea-contraception",
      text: "Severe/persistent diarrhoea: oral contraceptive efficacy may be reduced — advise additional contraception",
    },
  ],

  counselling: [
    "Take one 120mg capsule immediately before, during, or within 1 hour of each main meal containing fat",
    "Omit dose if meal is missed or fat-free",
    "Advise multivitamin supplement at bedtime or ≥2 hours after orlistat (fat-soluble vitamin absorption reduced)",
    "GI side effects (oily stools, spotting, urgency) are common — especially early and with high-fat meals",
    "Wearing dark clothing or using a pantyliner may help manage oily spotting",
    "Levothyroxine: take at a different time; attend thyroid monitoring",
    "Antiepileptic drugs: contact prescriber if seizure frequency or severity changes",
    "If severe diarrhoea: use additional contraception method; follow missed pill guidance",
    "Tell all prescribers (including GP) that you are taking orlistat",
    "Limit dietary fat to approximately 30% of total calories (~67g fat/day on 2,000 kcal diet)",
    "Distribute fat evenly across three main meals — do not have one high-fat meal",
    "Store blisters ≤25°C; bottles ≤30°C; protect from light and moisture",
    "Record batch number and expiry date",
    "Advise GP on initiation and at any significant clinical change",
    "Documentation retained 8 years",
  ],

  efficacyReview: {
    timepoint: "12 weeks",
    threshold: "≥5% weight loss",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Weight",
      "BMI",
      "Waist circumference",
      "Side effects",
      "Dietary compliance",
      "Physical activity",
      "HbA1c if T2DM; review antidiabetic medication doses following weight loss",
      "INR if on oral anticoagulants",
      "Thyroid function (TFTs) if on levothyroxine",
      "Renal function if symptoms of oxalate nephropathy develop (reduced urine output, severe flank pain)",
    ],
    discontinueIf: [
      "Less than 5% weight loss after 12 weeks",
      "Pregnancy",
      "Patient no longer meets inclusion criteria",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 28,
    drugSpecificReviewDays: 84,
    drugSpecificReviewLabel: "12-week efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss after 12 weeks — stop treatment, refer to GP, offer lifestyle support",
    sectionBReviewDays: 168,
    bmiFloorKgM2: null,
    titrationSchedule: undefined,
    maxEpisodesBeforeReview: null,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Rectal bleeding: investigate and refer to GP",
    "Signs of liver problems (jaundice, dark urine, severe abdominal pain): stop and refer urgently",
    "Oxalate nephropathy symptoms (severe flank pain, reduced urine output): refer",
    "Severe allergic reaction (rash, swelling, difficulty breathing): stop and refer urgently",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "No black triangle status",
  ],
};
